// Ficha y gestión de pacientes. Centro Medico → Clínica → Pacientes.
// El paciente es un partner (financiero.partner) + extensión clínica (medical.paciente).
// Alta/edición vía RPC med_guardar_paciente (atómico). Familiares vía REST.
import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, ArrowLeft, ClipboardList, KeyRound, Loader2, Pencil, Plus, Printer, Search, Trash2, UserPlus } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { SelectPais } from '@/components/ui/SelectPais';
import { useDialogoEnLinea } from '@/components/ui/PanelIntegrado';
import { getErrorMessage } from '@/lib/api/client';
import {
  AutoTextarea,
  Campo, Convenio, Familiar, ImageUpload, Pais, PacienteRow, Switch, TelefonoInput, calcEdad, fieldCls, GENEROS, med, medRpc,
} from './shared';
import { ConsentimientosPaciente } from './Consentimientos';
import { PanelArcoPaciente } from './DerechosArco';
import { FichaPaciente } from './FichaPaciente';
import { LOCALE } from '@/lib/utils/regional';

const vacio = {
  razon_social: '', identificacion: '', tipo_identificacion: 'cedula', email: '', telefono: '', celular: '',
  direccion: '', ciudad: '', provincia: '', pais: '', nacionalidad: '',
  codigo: '', fecha_nacimiento: '', genero: '', estado_civil: '', ocupacion: '', tipo_sangre: '',
  alergias: '', antecedentes: '', medicamentos_actuales: '', observaciones: '',
  aseguradora: '', numero_poliza: '', contacto_emergencia: '', telefono_emergencia: '', relacion_emergencia: '',
  empleador_id: '', cargo: '', centro_costo_id: '',
  foto_url: '',
  es_activo: true,
};
type Form = typeof vacio;

interface Detalle { paciente: Record<string, unknown>; partner: Record<string, unknown>; familiares: Familiar[] }

const ESTADO_CIVIL = ['Soltero/a', 'Casado/a', 'Divorciado/a', 'Viudo/a', 'Unión libre'];
const TABS = [
  ['filiacion', 'Datos de filiación'], ['contacto', 'Datos de Contacto'],
  ['laboral', 'Laboral'], ['seguro', 'Seguro Médico'], ['otros', 'Otros'],
] as const;
type TabKey = typeof TABS[number][0];

export function Pacientes() {
  const qc = useQueryClient();
  const dlg = useDialogoEnLinea();
  const [busqueda, setBusqueda] = useState('');
  const [filtro, setFiltro] = useState('');
  const [fcentro, setFcentro] = useState('');   // '' = todos, '__none__' = sin centro, o id del centro

  const { data: empleadores = [] } = useQuery<Convenio[]>({ queryKey: ['med-convenios', 'empresa'], queryFn: () => medRpc<Convenio[]>('med_convenios', { _tipo: 'empresa' }) });
  const { data: paises = [] } = useQuery<Pais[]>({ queryKey: ['med-paises'], queryFn: () => medRpc<Pais[]>('med_paises'), staleTime: Infinity });
  const { data: centros = [] } = useQuery<{ id: string; codigo: string; nombre: string }[]>({ queryKey: ['med-centros-costo'], queryFn: () => medRpc<{ id: string; codigo: string; nombre: string }[]>('med_centros_costo'), staleTime: 60_000 });
  const { data: rows = [], isFetching } = useQuery<PacienteRow[]>({
    queryKey: ['med-pacientes', filtro],
    queryFn: () => medRpc<PacienteRow[]>('med_pacientes', { _busqueda: filtro }),
  });

  const [abierto, setAbierto] = useState(false);
  const [fichaId, setFichaId] = useState<string | null>(null);
  const [editId, setEditId] = useState<string | null>(null);
  const [partnerId, setPartnerId] = useState<string | null>(null);
  const [form, setForm] = useState<Form>(vacio);
  const [tab, setTab] = useState<TabKey>('filiacion');
  const set = (k: keyof Form, v: string | boolean) => setForm((f) => ({ ...f, [k]: v }));

  // 979 · Prevención de duplicados: mientras se escribe el ALTA, busca pacientes
  // con la misma identificación, el mismo nombre o uno muy parecido.
  const [otraPersona, setOtraPersona] = useState(false);
  const [busqDup, setBusqDup] = useState({ nombre: '', identificacion: '' });
  useEffect(() => {
    const t = setTimeout(() => setBusqDup({ nombre: form.razon_social, identificacion: form.identificacion }), 400);
    return () => clearTimeout(t);
  }, [form.razon_social, form.identificacion]);
  useEffect(() => { setOtraPersona(false); }, [busqDup.nombre, busqDup.identificacion]);
  const { data: posiblesDup = [] } = useQuery<{ id: string; nombre: string; identificacion: string | null; codigo: string | null; coincide: 'identificacion' | 'nombre' | 'parecido'; similitud: number }[]>({
    queryKey: ['med-posibles-dup', busqDup.nombre, busqDup.identificacion],
    queryFn: () => medRpc('med_paciente_posibles_duplicados', { _nombre: busqDup.nombre || null, _identificacion: busqDup.identificacion || null }),
    enabled: abierto && !editId && (busqDup.nombre.trim().length >= 5 || busqDup.identificacion.trim().length >= 5),
  });
  const dupIdentidad = !editId && posiblesDup.some((d) => d.coincide === 'identificacion');
  const dupNombre = !editId && posiblesDup.some((d) => d.coincide === 'nombre');

  // Alta rápida de la empresa empleadora (partner cliente + convenio) desde la pestaña Laboral.
  const [nuevaEmp, setNuevaEmp] = useState<{ nombre: string; ruc: string } | null>(null);
  const crearEmpleador = useMutation({
    mutationFn: (d: { nombre: string; ruc: string }) =>
      medRpc<{ id: string; razon_social: string }>('med_convenio_empresa_rapido', { _data: { razon_social: d.nombre, identificacion: d.ruc || null } }),
    onSuccess: async (r) => {
      await qc.invalidateQueries({ queryKey: ['med-convenios', 'empresa'] });
      set('empleador_id', r.id);
      setNuevaEmp(null);
      toast.success('Empresa empleadora creada');
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const abrirNuevo = () => { setEditId(null); setPartnerId(null); setForm(vacio); setTab('filiacion'); setAbierto(true); };

  const abrirEditar = useMutation({
    mutationFn: (id: string) => medRpc<Detalle>('med_paciente', { _id: id }),
    onSuccess: (d) => {
      const p = d.partner as Record<string, string | null>;
      const c = d.paciente as Record<string, string | boolean | null>;
      setEditId(c.id as string); setPartnerId(p.id as string);
      setForm({
        razon_social: (p.razon_social as string) ?? '', identificacion: p.identificacion ?? '',
        tipo_identificacion: p.tipo_identificacion ?? 'cedula', email: p.email ?? '',
        telefono: p.telefono ?? '', celular: p.celular ?? '', direccion: p.direccion ?? '',
        ciudad: p.ciudad ?? '', provincia: p.provincia ?? '', pais: p.pais ?? '', nacionalidad: p.nacionalidad ?? '',
        codigo: (c.codigo as string) ?? '', fecha_nacimiento: (c.fecha_nacimiento as string) ?? '',
        genero: (c.genero as string) ?? '', estado_civil: (c.estado_civil as string) ?? '',
        ocupacion: (c.ocupacion as string) ?? '', tipo_sangre: (c.tipo_sangre as string) ?? '',
        alergias: (c.alergias as string) ?? '', antecedentes: (c.antecedentes as string) ?? '',
        medicamentos_actuales: (c.medicamentos_actuales as string) ?? '', observaciones: (c.observaciones as string) ?? '',
        aseguradora: (c.aseguradora as string) ?? '', numero_poliza: (c.numero_poliza as string) ?? '',
        contacto_emergencia: (c.contacto_emergencia as string) ?? '', telefono_emergencia: (c.telefono_emergencia as string) ?? '',
        relacion_emergencia: (c.relacion_emergencia as string) ?? '',
        empleador_id: (c.empleador_id as string) ?? '', cargo: (c.cargo as string) ?? '',
        centro_costo_id: (c.centro_costo_id as string) ?? '',
        foto_url: (c.foto_url as string) ?? '',
        es_activo: (c.es_activo as boolean) ?? true,
      });
      setTab('filiacion');
      setAbierto(true);
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const guardar = useMutation({
    // med_guardar_paciente no toca foto_url (pisa todas las demás columnas);
    // la foto se aplica aparte con med_paciente_foto si cambió.
    mutationFn: async () => {
      const { foto_url, ...datos } = form;
      const d = await medRpc<Detalle>('med_guardar_paciente', {
        _data: { ...datos, id: editId ?? undefined, partner_id: partnerId ?? undefined, confirmar_duplicado: otraPersona || undefined },
      });
      const c = d.paciente as Record<string, string | null>;
      if ((foto_url || '') !== ((c.foto_url as string) ?? '')) {
        await medRpc('med_paciente_foto', { _id: c.id, _foto: foto_url || null });
      }
      return d;
    },
    onSuccess: (d) => {
      toast.success('Paciente guardado');
      // si era alta, pasa a modo edición para poder añadir familiares
      const c = d.paciente as Record<string, string>; const p = d.partner as Record<string, string>;
      setEditId(c.id); setPartnerId(p.id);
      void qc.invalidateQueries({ queryKey: ['med-pacientes'] });
      void qc.invalidateQueries({ queryKey: ['med-ficha'] });
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const eliminar = useMutation({
    mutationFn: (id: string) => med.update('paciente', id, { deleted_at: new Date().toISOString() }),
    onSuccess: () => { toast.success('Paciente eliminado'); void qc.invalidateQueries({ queryKey: ['med-pacientes'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  // Filtro por centro de costo (cliente): '' todos · '__none__' sin centro · id del centro
  const visibles = fcentro
    ? rows.filter((p) => (fcentro === '__none__' ? !p.centro_costo_id : p.centro_costo_id === fcentro))
    : rows;

  const imprimir = () => {
    const esc = (s: unknown) => String(s ?? '').replace(/[&<>]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' }[c] as string));
    const centroLabel = fcentro === '__none__' ? 'Sin centro de costo'
      : fcentro ? (centros.find((c) => c.id === fcentro)?.nombre ?? '') : 'Todos los centros de costo';
    const fecha = new Date().toLocaleString(LOCALE);
    const filas = visibles.map((p, i) => `<tr>
      <td class="c">${i + 1}</td>
      <td>${esc(p.razon_social)}</td>
      <td>${esc(p.identificacion || '—')}</td>
      <td class="c">${esc(calcEdad(p.fecha_nacimiento))}</td>
      <td>${esc(p.telefono || p.celular || '—')}</td>
      <td>${esc(p.centro_nombre ? `${p.centro_codigo} · ${p.centro_nombre}` : '—')}</td>
      <td class="c">${esc(p.tipo_sangre || '—')}</td>
      <td class="c">${p.es_activo ? 'Activo' : 'Inactivo'}</td>
    </tr>`).join('');
    const html = `<!doctype html><html lang="es"><head><meta charset="utf-8"><title>Listado de pacientes</title>
      <style>
        *{font-family:Arial,Helvetica,sans-serif}
        h1{font-size:16px;margin:0 0 2px} .sub{font-size:12px;color:#555;margin:0 0 12px}
        table{border-collapse:collapse;width:100%;font-size:11px}
        th,td{border:1px solid #ccc;padding:4px 6px;text-align:left}
        th{background:#f0f0f0} .c{text-align:center}
        @media print{@page{margin:12mm}}
      </style></head><body>
      <h1>Listado de pacientes — Centro Médico</h1>
      <p class="sub">Centro de costo: <b>${esc(centroLabel)}</b> · Total: <b>${visibles.length}</b> · Emitido: ${esc(fecha)}</p>
      <table><thead><tr>
        <th class="c">#</th><th>Paciente</th><th>Identificación</th><th class="c">Edad</th>
        <th>Teléfono</th><th>Centro de costo</th><th class="c">Sangre</th><th class="c">Estado</th>
      </tr></thead><tbody>${filas || '<tr><td colspan="8" class="c">Sin pacientes</td></tr>'}</tbody></table>
      <script>window.onload=function(){window.print();}</script>
      </body></html>`;
    const win = window.open('', '_blank');
    if (!win) { toast.error('Permite las ventanas emergentes para imprimir'); return; }
    win.document.write(html); win.document.close();
  };

  // Ficha 360°: se abre desde el listado; el lápiz de su cabecera pasa al
  // formulario de edición y al cerrar éste se vuelve a la ficha.
  if (fichaId && !abierto) {
    return <FichaPaciente pacienteId={fichaId} onVolver={() => setFichaId(null)} onEditar={(id) => abrirEditar.mutate(id)} />;
  }

  return (
    <div className="p-6 space-y-4">
      {dlg.ui}
      {abierto ? (
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={() => setAbierto(false)}><ArrowLeft className="w-4 h-4 mr-1" /> Volver</Button>
          <h1 className="text-xl font-semibold">{editId ? 'Editar paciente' : 'Nuevo paciente'}</h1>
        </div>
      ) : (
        <div className="flex items-center justify-between">
          <h1 className="text-xl font-semibold">Pacientes</h1>
          <Button onClick={abrirNuevo}><Plus className="w-4 h-4 mr-1" /> Nuevo paciente</Button>
        </div>
      )}

      {!abierto && (<>
      <div className="flex flex-wrap items-center gap-2">
        <form className="flex gap-2 flex-1 min-w-[240px] max-w-xl" onSubmit={(e) => { e.preventDefault(); setFiltro(busqueda.trim()); }}>
          <input className={fieldCls} placeholder="Buscar por nombre, cédula o teléfono…" value={busqueda} onChange={(e) => setBusqueda(e.target.value)} />
          <Button type="submit" variant="outline"><Search className="w-4 h-4" /></Button>
        </form>
        <select className={`${fieldCls} w-auto min-w-[220px]`} value={fcentro} onChange={(e) => setFcentro(e.target.value)} title="Filtrar por centro de costo">
          <option value="">Todos los centros de costo</option>
          <option value="__none__">Sin centro de costo</option>
          {centros.map((c) => <option key={c.id} value={c.id}>{c.codigo} · {c.nombre}</option>)}
        </select>
        <Button variant="outline" onClick={imprimir} title="Imprimir el listado filtrado"><Printer className="w-4 h-4 mr-1" /> Imprimir</Button>
      </div>

      <Card>
        <CardContent className="overflow-x-auto pt-4">
          {isFetching && <div className="py-6 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" /></div>}
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-xs text-muted-foreground">
              <th className="py-2 pr-3">Paciente</th><th className="py-2 pr-3">Identificación</th><th className="py-2 pr-3">Edad</th>
              <th className="py-2 pr-3">Teléfono</th><th className="py-2 pr-3">Centro de costo</th><th className="py-2 pr-3">Sangre</th><th className="py-2 pr-3">Estado</th><th className="py-2"></th>
            </tr></thead>
            <tbody>
              {visibles.length === 0 && <tr><td colSpan={8} className="py-8 text-center text-muted-foreground">Sin pacientes</td></tr>}
              {visibles.map((p) => (
                <tr key={p.id} className="border-b last:border-0 hover:bg-muted/40">
                  <td className="py-2 pr-3 font-medium">{p.razon_social}</td>
                  <td className="py-2 pr-3 font-mono text-muted-foreground">{p.identificacion || '—'}</td>
                  <td className="py-2 pr-3">{calcEdad(p.fecha_nacimiento)}</td>
                  <td className="py-2 pr-3">{p.telefono || p.celular || '—'}</td>
                  <td className="py-2 pr-3">{p.centro_nombre ? <span title={p.centro_nombre}>{p.centro_codigo} · {p.centro_nombre}</span> : '—'}</td>
                  <td className="py-2 pr-3">{p.tipo_sangre || '—'}</td>
                  <td className="py-2 pr-3">{p.es_activo ? 'Activo' : 'Inactivo'}</td>
                  <td className="py-2 text-right whitespace-nowrap">
                    <button className="inline-flex w-7 h-7 items-center justify-center rounded-md hover:bg-muted" title="Ver ficha" aria-label="Ver ficha" onClick={() => setFichaId(p.id)}><ClipboardList className="w-4 h-4" /></button>
                    <button className="inline-flex w-7 h-7 items-center justify-center rounded-md hover:bg-muted ml-1" title="Editar" aria-label="Editar" onClick={() => abrirEditar.mutate(p.id)}><Pencil className="w-4 h-4" /></button>
                    <button className="inline-flex w-7 h-7 items-center justify-center rounded-md text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 ml-1" title="Eliminar" aria-label="Eliminar"
                      onClick={async () => { if (!(await dlg.confirmar(`¿Eliminar a "${p.razon_social}"?`, { peligro: true }))) return; eliminar.mutate(p.id); }}><Trash2 className="w-4 h-4" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>
      </>)}

      {abierto && (
        <Card>
          <CardContent className="pt-4">
          <div className="space-y-4">
            {/* Pestañas del formulario */}
            <div className="flex flex-wrap gap-1 border-b">
              {TABS.map(([k, l]) => (
                <button key={k} type="button" onClick={() => setTab(k)}
                  className={`px-3 py-2 text-sm -mb-px border-b-2 ${tab === k ? 'border-primary text-primary font-medium' : 'border-transparent text-muted-foreground hover:text-foreground'}`}>
                  {l}
                </button>
              ))}
            </div>

            {tab === 'filiacion' && !editId && posiblesDup.length > 0 && (
              <div className={`rounded-md border p-3 text-sm ${dupIdentidad ? 'border-red-300 bg-red-50 text-red-900 dark:border-red-800 dark:bg-red-950/30 dark:text-red-200' : 'border-amber-300 bg-amber-50 text-amber-900 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-200'}`}>
                <p className="flex items-center gap-1.5 font-medium"><AlertTriangle className="h-4 w-4 shrink-0" />
                  {dupIdentidad ? 'Ya existe un paciente con esta identificación: no se puede crear otra ficha.' : '¿Es alguno de estos pacientes? Evite crear una ficha duplicada.'}</p>
                <ul className="mt-2 space-y-1">
                  {posiblesDup.map((d) => (
                    <li key={d.id} className="flex flex-wrap items-center gap-2">
                      <span className="font-medium">{d.nombre}</span>
                      <span className="text-xs opacity-80">{d.identificacion ?? 's/i'}{d.codigo ? ` · historia ${d.codigo}` : ''}</span>
                      <span className="rounded-full bg-background/70 px-1.5 py-0.5 text-[10px]">
                        {d.coincide === 'identificacion' ? 'misma identificación' : d.coincide === 'nombre' ? 'mismo nombre' : `nombre parecido ${Math.round(Number(d.similitud) * 100)} %`}</span>
                      <button type="button" className="text-xs font-medium underline" onClick={() => abrirEditar.mutate(d.id)}>Abrir esta ficha</button>
                    </li>
                  ))}
                </ul>
                {!dupIdentidad && dupNombre && (
                  <label className="mt-2 flex items-center gap-2 text-xs font-medium">
                    <input type="checkbox" checked={otraPersona} onChange={(e) => setOtraPersona(e.target.checked)} />
                    Es otra persona con el mismo nombre: crear la ficha de todos modos
                  </label>
                )}
              </div>
            )}
            {tab === 'filiacion' && (
              <div className="grid grid-cols-2 gap-3">
                <Campo label="Nombre completo *"><input className={fieldCls} value={form.razon_social} onChange={(e) => set('razon_social', e.target.value)} /></Campo>
                <Campo label="Identificación *"><input className={fieldCls} value={form.identificacion} onChange={(e) => set('identificacion', e.target.value)} /></Campo>
                <Campo label="Tipo de identificación">
                  <select className={fieldCls} value={form.tipo_identificacion} onChange={(e) => set('tipo_identificacion', e.target.value)}>
                    <option value="cedula">Cédula</option><option value="ruc">RUC</option><option value="pasaporte">Pasaporte</option>
                  </select>
                </Campo>
                <Campo label="Nº historia clínica"><input className={fieldCls} value={form.codigo} onChange={(e) => set('codigo', e.target.value)} /></Campo>
                <Campo label="Fecha de nacimiento"><input type="date" className={fieldCls} value={form.fecha_nacimiento} onChange={(e) => set('fecha_nacimiento', e.target.value)} /></Campo>
                <Campo label="Género">
                  <select className={fieldCls} value={form.genero} onChange={(e) => set('genero', e.target.value)}>
                    <option value="">—</option>{GENEROS.map((g) => <option key={g.v} value={g.v}>{g.l}</option>)}
                  </select>
                </Campo>
                <Campo label="Estado civil">
                  <select className={fieldCls} value={form.estado_civil} onChange={(e) => set('estado_civil', e.target.value)}>
                    <option value="">—</option>{ESTADO_CIVIL.map((v) => <option key={v} value={v}>{v}</option>)}
                  </select>
                </Campo>
                <Campo label="Nacionalidad">
                  <select className={fieldCls} value={form.nacionalidad} onChange={(e) => set('nacionalidad', e.target.value)}>
                    <option value="">—</option>{paises.map((p) => <option key={p.codigo} value={p.nacionalidad}>{p.nacionalidad}</option>)}
                  </select>
                </Campo>
                <Campo label="Ocupación"><input className={fieldCls} value={form.ocupacion} onChange={(e) => set('ocupacion', e.target.value)} /></Campo>
                <Campo label="Centro de costo (misión)">
                  <select className={fieldCls} value={form.centro_costo_id} onChange={(e) => set('centro_costo_id', e.target.value)}>
                    <option value="">—</option>{centros.map((c) => <option key={c.id} value={c.id}>{c.codigo} · {c.nombre}</option>)}
                  </select>
                </Campo>
                <div className="col-span-2">
                  <ImageUpload label="Foto del paciente" value={form.foto_url} onChange={(v) => set('foto_url', v)} />
                </div>
              </div>
            )}

            {tab === 'contacto' && (
              <div className="grid grid-cols-2 gap-3">
                <Campo label="Teléfono"><TelefonoInput value={form.telefono} onChange={(v) => set('telefono', v)} paises={paises} /></Campo>
                <Campo label="Celular"><TelefonoInput value={form.celular} onChange={(v) => set('celular', v)} paises={paises} /></Campo>
                <Campo label="Email"><input className={fieldCls} value={form.email} onChange={(e) => set('email', e.target.value)} /></Campo>
                <Campo label="País">
                  <SelectPais className={fieldCls} value={form.pais} onChange={(v) => set('pais', v)} />
                </Campo>
                <Campo label="Ciudad"><input className={fieldCls} value={form.ciudad} onChange={(e) => set('ciudad', e.target.value)} /></Campo>
                <Campo label="Provincia"><input className={fieldCls} value={form.provincia} onChange={(e) => set('provincia', e.target.value)} /></Campo>
                <Campo label="Dirección" className="col-span-2"><input className={fieldCls} value={form.direccion} onChange={(e) => set('direccion', e.target.value)} /></Campo>
              </div>
            )}

            {tab === 'laboral' && (
              <div className="grid grid-cols-2 gap-3">
                <Campo label="Empresa empleadora">
                  <div className="flex gap-2">
                    <select className={fieldCls} value={form.empleador_id} onChange={(e) => set('empleador_id', e.target.value)}>
                      <option value="">— Ninguna —</option>{empleadores.map((c) => <option key={c.id} value={c.id}>{c.razon_social}</option>)}
                    </select>
                    <Button type="button" variant="outline" size="sm" className="shrink-0" onClick={() => setNuevaEmp(nuevaEmp ? null : { nombre: '', ruc: '' })}>
                      <Plus className="w-4 h-4 mr-1" /> Nueva
                    </Button>
                  </div>
                </Campo>
                <Campo label="Cargo"><input className={fieldCls} value={form.cargo} onChange={(e) => set('cargo', e.target.value)} /></Campo>
                {nuevaEmp && (
                  <div className="col-span-2 rounded-md border bg-muted/30 p-2 space-y-2">
                    <p className="text-xs font-semibold">Nueva empresa empleadora</p>
                    <div className="grid grid-cols-2 gap-3">
                      <Campo label="Nombre / razón social *"><input className={fieldCls} autoFocus value={nuevaEmp.nombre} onChange={(e) => setNuevaEmp({ ...nuevaEmp, nombre: e.target.value })} /></Campo>
                      <Campo label="RUC (opcional)"><input className={fieldCls} value={nuevaEmp.ruc} onChange={(e) => setNuevaEmp({ ...nuevaEmp, ruc: e.target.value })} placeholder="0990000000001" /></Campo>
                    </div>
                    <div className="flex justify-end gap-2">
                      <Button type="button" variant="outline" size="sm" onClick={() => setNuevaEmp(null)}>Cancelar</Button>
                      <Button type="button" size="sm" isLoading={crearEmpleador.isPending} disabled={!nuevaEmp.nombre.trim()} onClick={() => crearEmpleador.mutate(nuevaEmp)}>Crear y seleccionar</Button>
                    </div>
                  </div>
                )}
                <p className="col-span-2 text-[11px] text-muted-foreground">
                  La empresa empleadora también se gestiona en <b>Centro Médico → Catálogos → Convenios</b> (tipo «empresa»).
                  Estos datos alimentan el certificado de reposo (institución y puesto de trabajo).
                </p>
              </div>
            )}

            {tab === 'seguro' && (
              <div className="grid grid-cols-2 gap-3">
                <Campo label="Aseguradora"><input className={fieldCls} value={form.aseguradora} onChange={(e) => set('aseguradora', e.target.value)} /></Campo>
                <Campo label="Nº de póliza"><input className={fieldCls} value={form.numero_poliza} onChange={(e) => set('numero_poliza', e.target.value)} /></Campo>
              </div>
            )}

            {tab === 'otros' && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <Campo label="Tipo de sangre"><input className={fieldCls} value={form.tipo_sangre} onChange={(e) => set('tipo_sangre', e.target.value)} placeholder="A+, O-, …" /></Campo>
                  <Campo label="Alergias" className="col-span-2"><AutoTextarea value={form.alergias} onChange={(e) => set('alergias', e.target.value)} /></Campo>
                  <Campo label="Antecedentes" className="col-span-2"><AutoTextarea value={form.antecedentes} onChange={(e) => set('antecedentes', e.target.value)} /></Campo>
                  <Campo label="Medicamentos actuales" className="col-span-2"><AutoTextarea value={form.medicamentos_actuales} onChange={(e) => set('medicamentos_actuales', e.target.value)} /></Campo>
                  <Campo label="Observaciones" className="col-span-2"><AutoTextarea value={form.observaciones} onChange={(e) => set('observaciones', e.target.value)} /></Campo>
                </div>
                <Seccion titulo="Contacto de emergencia">
                  <div className="grid grid-cols-3 gap-3">
                    <Campo label="Nombre"><input className={fieldCls} value={form.contacto_emergencia} onChange={(e) => set('contacto_emergencia', e.target.value)} /></Campo>
                    <Campo label="Teléfono"><TelefonoInput value={form.telefono_emergencia} onChange={(v) => set('telefono_emergencia', v)} paises={paises} /></Campo>
                    <Campo label="Relación"><input className={fieldCls} value={form.relacion_emergencia} onChange={(e) => set('relacion_emergencia', e.target.value)} /></Campo>
                  </div>
                </Seccion>
              </div>
            )}

            {editId && <ConsentimientosPaciente pacienteId={editId} />}
            {editId && <PanelArcoPaciente pacienteId={editId} nombre={form.razon_social} />}
            {editId && <Familiares pacienteId={editId} />}
            {editId && <AccesoPortal pacienteId={editId} sugerido={form.identificacion} email={form.email} />}

            <div className="flex items-center justify-between border-t pt-3">
              <Switch checked={form.es_activo} onChange={(v) => set('es_activo', v)} label="Activo" />
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setAbierto(false)}>Cerrar</Button>
                <Button isLoading={guardar.isPending} disabled={!form.razon_social.trim() || !form.identificacion.trim() || dupIdentidad || (dupNombre && !otraPersona)}
                  title={dupIdentidad ? 'Ya existe un paciente con esta identificación' : dupNombre && !otraPersona ? 'Confirme que es otra persona con el mismo nombre' : undefined} onClick={() => guardar.mutate()}>Guardar</Button>
              </div>
            </div>
          </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function Seccion({ titulo, children }: { titulo: string; children: React.ReactNode }) {
  return (
    <section className="space-y-2">
      <h4 className="text-sm font-semibold text-muted-foreground">{titulo}</h4>
      {children}
    </section>
  );
}

// ---- Acceso al portal del paciente ----
function AccesoPortal({ pacienteId, sugerido, email }: { pacienteId: string; sugerido: string; email: string }) {
  const [abrir, setAbrir] = useState(false);
  const [f, setF] = useState({ username: '', email: '', password: '' });

  const crear = useMutation({
    mutationFn: () => medRpc('med_crear_acceso_paciente', { _paciente_id: pacienteId, _username: f.username, _email: f.email, _password: f.password }),
    onSuccess: () => { toast.success('Acceso al portal creado'); setAbrir(false); setF({ username: '', email: '', password: '' }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  if (!abrir) {
    return (
      <Seccion titulo="Portal del paciente">
        <Button size="sm" variant="outline" onClick={() => { setAbrir(true); setF({ username: sugerido || '', email: email || '', password: '' }); }}>
          <KeyRound className="w-4 h-4 mr-1" /> Crear acceso al portal
        </Button>
      </Seccion>
    );
  }
  return (
    <Seccion titulo="Portal del paciente">
      <div className="grid grid-cols-12 gap-2 items-end">
        <div className="col-span-4"><Campo label="Usuario *"><input className={fieldCls} value={f.username} onChange={(e) => setF({ ...f, username: e.target.value })} /></Campo></div>
        <div className="col-span-4"><Campo label="Email *"><input className={fieldCls} value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} /></Campo></div>
        <div className="col-span-4"><Campo label="Contraseña *"><input type="password" className={fieldCls} value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} /></Campo></div>
        <div className="col-span-12 flex gap-2">
          <Button size="sm" variant="outline" onClick={() => setAbrir(false)}>Cancelar</Button>
          <Button size="sm" isLoading={crear.isPending} disabled={!f.username || !f.email || !f.password} onClick={() => crear.mutate()}>Crear acceso</Button>
        </div>
      </div>
    </Seccion>
  );
}

// ---- Familiares / tutores (sub-tabla, solo en edición) ----
const famVacio = { nombre: '', parentesco: '', identificacion: '', telefono: '', email: '', es_tutor: false };

function Familiares({ pacienteId }: { pacienteId: string }) {
  const qc = useQueryClient();
  const { data: rows = [] } = useQuery<Familiar[]>({
    queryKey: ['med-familiares', pacienteId],
    queryFn: () => medRpc<Familiar[]>('med_familiares', { _paciente_id: pacienteId }),
  });
  const [f, setF] = useState<typeof famVacio>(famVacio);

  const agregar = useMutation({
    mutationFn: () => med.create('paciente_familiar', { ...f, paciente_id: pacienteId, parentesco: f.parentesco || null, identificacion: f.identificacion || null, telefono: f.telefono || null, email: f.email || null }),
    onSuccess: () => { setF(famVacio); void qc.invalidateQueries({ queryKey: ['med-familiares', pacienteId] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const quitar = useMutation({
    mutationFn: (id: string) => med.update('paciente_familiar', id, { deleted_at: new Date().toISOString() }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['med-familiares', pacienteId] }),
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  return (
    <Seccion titulo="Familiares / tutores">
      {rows.length > 0 && (
        <ul className="text-sm divide-y border rounded-md">
          {rows.map((r) => (
            <li key={r.id} className="flex items-center justify-between px-3 py-1.5">
              <span>{r.nombre} {r.parentesco && <span className="text-muted-foreground">· {r.parentesco}</span>} {r.es_tutor && <span className="ml-1 rounded bg-primary/10 px-1.5 py-0.5 text-xs text-primary">tutor</span>}</span>
              <button className="text-red-500 hover:text-urgente" title="Quitar" aria-label="Quitar" onClick={() => quitar.mutate(r.id)}><Trash2 className="w-4 h-4" /></button>
            </li>
          ))}
        </ul>
      )}
      <div className="grid grid-cols-12 gap-2 items-end">
        <div className="col-span-4"><Campo label="Nombre"><input className={fieldCls} value={f.nombre} onChange={(e) => setF({ ...f, nombre: e.target.value })} /></Campo></div>
        <div className="col-span-3"><Campo label="Parentesco"><input className={fieldCls} value={f.parentesco} onChange={(e) => setF({ ...f, parentesco: e.target.value })} /></Campo></div>
        <div className="col-span-3"><Campo label="Teléfono"><input className={fieldCls} value={f.telefono} onChange={(e) => setF({ ...f, telefono: e.target.value })} /></Campo></div>
        <div className="col-span-2 flex items-center gap-2 pb-2">
          <Switch checked={f.es_tutor} onChange={(v) => setF({ ...f, es_tutor: v })} label="Tutor" />
        </div>
        <div className="col-span-12">
          <Button size="sm" variant="outline" isLoading={agregar.isPending} disabled={!f.nombre.trim()} onClick={() => agregar.mutate()}>
            <UserPlus className="w-4 h-4 mr-1" /> Agregar familiar
          </Button>
        </div>
      </div>
    </Seccion>
  );
}
